<template>
    <hover-card
        :for-multiselect="forMultiselect"
        :title="title"
        :is-loading-metrics="isLoadingMetrics"
        :has-metrics-error="hasMetricsError"
        :metrics="metrics"
        :height="height"
        :metrics-title="$t('Metrics')"
        :metrics-subtitle="$t('Last 30 days')">
        <template #actions>
            <div class="countable-hover-card__actions">
                <pendo-tag
                    class="countable-hover-card__tag"
                    :style="{ color: productAreaTagInfo.color, backgroundColor: productAreaTagInfo.backgroundColor }"
                    :label="productAreaTagInfo.label"
                    size="small"
                    subtle />
                <div class="countable-hover-card__actions-buttons-wrapper">
                    <star
                        :id="id"
                        v-pendo-tooltip="{ content: $t('Favorite'), position: 'top' }"
                        class="segment-details-drawer--star-icon pendo-button"
                        :type="starType" />
                    <pendo-router-link
                        :to="viewDetailsLink"
                        class="countable-hover-card__view-details-link"
                        target="_blank">
                        <pendo-button
                            v-pendo-tooltip="{ content: $t('View details'), position: 'top' }"
                            class="countable-hover-card__view-details-button"
                            type="tertiary"
                            @click="onViewDetailsClick">
                            <template #default>
                                <pendo-icon
                                    type="external-link"
                                    size="16"
                                    stroke-width="2" />
                            </template>
                        </pendo-button>
                    </pendo-router-link>
                </div>
            </div>
        </template>
        <template #subtitle>
            <div
                v-if="appName"
                class="countable-hover-card__page-name">
                <pendo-app-icon
                    v-if="app.id"
                    :app="app"
                    :size="12" />
                <span>
                    {{ appName }}
                </span>
            </div>
        </template>
        <template #body>
            <div
                :style="descriptionStyles"
                class="countable-hover-card__description"
                :class="{ 'no-description': !description }">
                {{ description ? description : $t('countableHoverCard.No description added.') }}
            </div>
        </template>
    </hover-card>
</template>

<script>
import { PendoButton, PendoIcon, PendoTag, PendoTooltip, PendoAppIcon } from '@pendo/components';
import get from 'lodash/get';
import { mapGetters } from 'vuex';
import HoverCard from '@/common/hover-card/hover-card.vue';
import useCountableLookup from '@/common/use/use-countable-lookup/use-countable-lookup';
import Star from '@/common/star/star';
import { getGroupColorByCSSClass } from '@/groups/group-color-picker/group-color-service';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { toRefs } from 'vue';
import PageMetricsAggregation from './aggs/page-metrics.aggregation';
import FeatureMetricsAggregation from './aggs/feature-metrics.aggregation';
import TrackEventMetricsAggregation from './aggs/track-event-metrics.aggregation';
import { v4 as uuid } from 'uuid';
import capitalize from 'lodash/capitalize';
import {
    createMetricsObservable,
    handleMetricsResponse,
    sendHoverCardTrackEvent,
} from '@/common/hover-card/hover-card-metrics.utils';
import { LOADING_STATES } from '@/constants';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

const aggByType = {
    page: PageMetricsAggregation,
    feature: FeatureMetricsAggregation,
    trackEvent: TrackEventMetricsAggregation,
};

export default {
    name: 'CountableHoverCard',
    directives: {
        PendoTooltip,
    },
    components: {
        HoverCard,
        PendoButton,
        PendoIcon,
        PendoAppIcon,
        PendoRouterLink,
        PendoTag,
        Star,
    },
    props: {
        type: {
            type: String,
            required: true,
            validator: (value) => ['page', 'feature', 'trackEvent'].includes(value),
        },
        id: {
            type: String,
            required: true,
        },
        forMultiselect: {
            type: Boolean,
            default: false,
        },
        height: {
            type: String,
            default: undefined,
        },
    },
    emits: ['view-details-click'],
    setup (props) {
        const { id, type } = toRefs(props);
        const { countable } = useCountableLookup(id, type);

        return {
            countable,
        };
    },
    data () {
        return {
            occurrencesValue: 0,
            visitorsValue: 0,
            accountsValue: 0,
            isLoadingMetrics: true,
            hasMetricsError: false,
            descriptionStyles: { 'height': '56.889px', '-webkit-line-clamp': 3 },
        };
    },
    computed: {
        ...mapGetters({
            appById: 'apps/getAppById',
            hasAccountLevelAnalyticsEnabled: 'subscription/hasAccountLevelAnalyticsEnabled',
        }),
        starType () {
            if (this.type === 'trackEvent') {
                return 'TrackType';
            }

            return capitalize(this.type);
        },
        productAreaTagInfo () {
            const productArea = get(this.countable, 'group');
            const productAreaColorClass = get(productArea, 'color', '.groupColorUngrouped') || '.groupColorUngrouped';
            const color = getGroupColorByCSSClass(productAreaColorClass);

            return {
                label: get(productArea, 'name', this.$t('No Product Area')) || this.$t('No Product Area'),
                color,
                backgroundColor: `${color}1a`,
            };
        },
        title () {
            return get(this.countable, 'name', '');
        },
        description () {
            return get(this.countable, 'description', null);
        },
        app () {
            return this.appById(get(this.countable, 'appId')) || {};
        },
        appName () {
            return this.app?.displayName || this.app?.name || this.$t('countableHoverCard.Unknown App');
        },
        occurrencesMetric () {
            switch (this.type) {
                case 'page':
                    return {
                        label: this.$t(
                            'countableHoverCard.occurrencesMetric.Page {occurrences, plural, one {view} other {views}}',
                            { occurrences: this.occurrencesValue },
                        ),
                        icon: 'file',
                        value: this.occurrencesValue,
                    };
                case 'feature':
                    return {
                        label: this.$t(
                            'countableHoverCard.occurrencesMetric.Feature {occurrences, plural, one {click} other {clicks}}',
                            { occurrences: this.occurrencesValue },
                        ),
                        icon: 'feature',
                        value: this.occurrencesValue,
                    };
                case 'trackEvent':
                default:
                    return {
                        label: this.$t(
                            'countableHoverCard.occurrencesMetric.Track {occurrences, plural, one {event} other {events}}',
                            { occurrences: this.occurrencesValue },
                        ),
                        icon: 'zap',
                        value: this.occurrencesValue,
                    };
            }
        },
        visitorsMetric () {
            return {
                label: this.$t(
                    'countableHoverCard.visitorsMetric.Unique {visitorCount, plural, one {visitor} other {visitors}}',
                    { visitorCount: this.visitorsValue },
                ),
                icon: 'user',
                value: this.visitorsValue,
            };
        },
        accountsMetric () {
            if (!this.hasAccountLevelAnalyticsEnabled || !this.$productLine(PRODUCT_LINES.CORE, 'accountsUIEnabled')) {
                return null;
            }

            return {
                label: this.$t(
                    'countableHoverCard.accountsMetric.Unique {accountCount, plural, one {account} other {accounts}}',
                    { accountCount: this.accountsValue },
                ),
                icon: 'users',
                value: this.accountsValue,
            };
        },
        metrics () {
            return [this.occurrencesMetric, this.visitorsMetric, this.accountsMetric].filter(Boolean);
        },
        viewDetailsLink () {
            switch (this.type) {
                case 'page':
                    return { name: 'PageDetails', params: { pageId: this.id } };
                case 'feature':
                    return { name: 'FeatureDetails', params: { featureId: this.id } };
                case 'trackEvent':
                    return { name: 'TrackEventsDetails', params: { trackTypeId: this.id } };
                default:
                    return {};
            }
        },
    },
    watch: {
        countable: {
            handler () {
                this.$nextTick(() => {
                    this.adjustDescriptionHeight();
                });
            },
        },
    },
    mounted () {
        this.$nextTick(() => {
            this.adjustDescriptionHeight();
        });
    },
    created () {
        this.STREAM_ID = uuid();

        const metrics$ = createMetricsObservable({
            aggregation: (params) => aggByType[params.type],
            streamId: this.STREAM_ID,
            componentParams: { id: 'id', type: 'type' },
            component: this,
            store: this.$store,
            onAggStart: () => {
                this.aggStartTime = Date.now();
            },
        });

        this.metricsSubscription = metrics$.subscribe((response) => {
            const updates = handleMetricsResponse(response);
            Object.assign(this, updates);

            if (response.status === LOADING_STATES.RESOLVED) {
                sendHoverCardTrackEvent({
                    eventType: this.type,
                    aggStartTime: this.aggStartTime,
                    pageLocation: this.$route?.name,
                });
            }
        });
    },
    unmounted () {
        this.metricsSubscription?.unsubscribe();
    },
    methods: {
        adjustDescriptionHeight () {
            const title = document.querySelector('.hover-card__title');

            if (!title) {
                return;
            }

            if (!this.description) {
                this.descriptionStyles = {};

                return;
            }

            const titleLineHeight = parseFloat(getComputedStyle(title).lineHeight);
            const titleHeight = title.offsetHeight;
            const numTitleLines = Math.floor(titleHeight / titleLineHeight);

            const isMultilineTitle = numTitleLines > 1;

            if (this.type === 'feature') {
                this.descriptionStyles = isMultilineTitle
                    ? { 'height': '56.889px', '-webkit-line-clamp': 3 }
                    : { 'height': '75.852px', '-webkit-line-clamp': 4 };
            } else {
                this.descriptionStyles = isMultilineTitle
                    ? { 'height': '75.852px', '-webkit-line-clamp': 4 }
                    : { 'height': '94.815px', '-webkit-line-clamp': 5 };
            }
        },
        onViewDetailsClick () {
            this.$emit('view-details-click');
        },
    },
};
</script>

<style lang="less" scoped>
.countable-hover-card {
    &__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        height: 23px;

        &-buttons-wrapper {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    &__tag {
        max-width: 197px;

        :deep(> span) {
            text-transform: none;
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 400;
        }
    }

    &__view-details-link {
        display: flex;
        align-items: center;
    }

    &__view-details-button {
        height: 16px;
        color: var(--palette-gray-70);

        &:hover {
            color: var(--palette-gray-100);
        }
    }

    &__page-name {
        display: flex;
        align-items: center;
        color: var(--palette-gray-70);
        font-size: 12.64px;
        font-weight: 400;
        padding-top: 8px;
        gap: 8px;
        span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &__description {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 18.963px;
        font-size: 12.642px;

        &.no-description {
            font-style: italic;
            color: var(--palette-gray-70);
        }
    }
}
</style>
